import { CardElement, GameCard, PlayStyle } from "@/types/battle"
import { maxLevelForRank } from "@/constants/game.constants"
import { assetUrl } from "@/constants/assetUrl"

const GUARDIANS_PHOTO = "https://backend.daks.by/photo/GUARDIANS"

export type CardImageSource = string

export interface ElementCardAssets {
  heroImage?: CardImageSource | null
  footerBackground?: CardImageSource | null
  elementIcon?: CardImageSource | null
  powerIcon?: CardImageSource | null
  styleIcon?: CardImageSource | null
  footerDecoration?: CardImageSource | null
}

export const GLOBAL_POWER_ICON = `${GUARDIANS_PHOTO}/1780030654750_3445263781440746_power-g.png`
export const SHAPE_PILL_RATIO = 631 / 205

const LOCAL_POWER_ICON = assetUrl("icons/power.png")

const LOCAL_ELEMENT_ICONS: Record<CardElement, string> = {
  LIGHTNING: assetUrl("icons/lightning.png"),
  FIRE: assetUrl("icons/fire.png"),
  WATER: assetUrl("icons/water.png"),
  EARTH: assetUrl("icons/earth.png"),
  WIND: assetUrl("icons/wind.png"),
  ICE: assetUrl("icons/ice.png"),
}

const LOCAL_STYLE_ICONS: Record<PlayStyle, string> = {
  ATTACK: assetUrl("icons/attack.png"),
  DEFENSE: assetUrl("icons/defense.png"),
  SPEED: assetUrl("icons/speed.png"),
}

const LOCAL_ELEMENT_BACKGROUNDS: Record<CardElement, string> = {
  LIGHTNING: assetUrl("backgrounds/lightning.png"),
  FIRE: assetUrl("backgrounds/fire.png"),
  WATER: assetUrl("backgrounds/water.png"),
  EARTH: assetUrl("backgrounds/earth.png"),
  WIND: assetUrl("backgrounds/wind.png"),
  ICE: assetUrl("backgrounds/ice.png"),
}

const LOCAL_ELEMENT_HEROES_STATIC: Record<CardElement, string> = {
  LIGHTNING: assetUrl("heroes/lightning-ninja.png"),
  FIRE: assetUrl("heroes/fire-ninja.png"),
  WATER: assetUrl("heroes/water-ninja.png"),
  EARTH: assetUrl("heroes/earth-ninja.png"),
  WIND: assetUrl("heroes/wind-ninja.png"),
  ICE: assetUrl("heroes/ice-ninja.png"),
}

const LOCAL_ELEMENT_HEROES_ANIMATED: Partial<Record<CardElement, string>> = {
  FIRE: assetUrl("heroes/fire-ninja-anim.webp"),
  WATER: assetUrl("heroes/water-ninja-anim.webp"),
  EARTH: assetUrl("heroes/earth-ninja-anim.webp"),
  ICE: assetUrl("heroes/ice-ninja-anim.webp"),
  LIGHTNING: assetUrl("heroes/lightning-ninja-anim.webp"),
  WIND: assetUrl("heroes/wind-ninja-anim.webp"),
}

export const STYLE_ICON_URLS: Record<PlayStyle, string> = {
  ATTACK: `${GUARDIANS_PHOTO}/1780030543781_3407453608743267_attack-g.png`,
  DEFENSE: `${GUARDIANS_PHOTO}/1780030559396_548307300552308_def-g.png`,
  SPEED: `${GUARDIANS_PHOTO}/1780030669905_7950343317266595_speed-g.png`,
}

function buildElementAssets(element: CardElement): ElementCardAssets {
  return {
    heroImage: LOCAL_ELEMENT_HEROES_STATIC[element],
    footerBackground: LOCAL_ELEMENT_BACKGROUNDS[element],
    elementIcon: LOCAL_ELEMENT_ICONS[element],
    powerIcon: LOCAL_POWER_ICON,
  }
}

export function isCardAtRankMaxLevel(
  card: Pick<GameCard, "level" | "colorRank">
): boolean {
  return card.level >= maxLevelForRank(card.colorRank)
}

export function resolveCardHeroImage(
  card: Pick<GameCard, "element" | "level" | "colorRank">
): string {
  const staticHero = LOCAL_ELEMENT_HEROES_STATIC[card.element]
  if (!isCardAtRankMaxLevel(card)) return staticHero
  return LOCAL_ELEMENT_HEROES_ANIMATED[card.element] ?? staticHero
}

export function resolveCardElementAssets(
  card: Pick<GameCard, "element" | "level" | "colorRank">,
  overrides?: ElementCardAssets
): ElementCardAssets {
  const base = CARD_ELEMENT_ASSETS[card.element]
  return {
    ...base,
    ...overrides,
    heroImage: overrides?.heroImage ?? resolveCardHeroImage(card),
  }
}

export const CARD_ELEMENT_ASSETS: Record<CardElement, ElementCardAssets> = {
  LIGHTNING: buildElementAssets("LIGHTNING"),
  FIRE: buildElementAssets("FIRE"),
  WATER: buildElementAssets("WATER"),
  EARTH: buildElementAssets("EARTH"),
  WIND: buildElementAssets("WIND"),
  ICE: buildElementAssets("ICE"),
}

export const CARD_FOOTER_GRADIENTS: Record<CardElement, [string, string]> = {
  FIRE: ["#1a0a08", "#3d1510"],
  WATER: ["#081018", "#0c2840"],
  EARTH: ["#0a1208", "#142810"],
  LIGHTNING: ["#12100a", "#2a2410"],
  WIND: ["#081210", "#0c2820"],
  ICE: ["#081018", "#0a2838"],
}

export const STYLE_ICON_FALLBACK: Record<PlayStyle, string> = {
  ATTACK: "⚔️",
  DEFENSE: "🛡️",
  SPEED: "💨",
}

export function resolveCardImageSource(
  source?: CardImageSource | null
): string | null {
  if (!source) return null
  return source
}

export function cardImageRecyclingKey(source?: CardImageSource | null): string {
  return source ?? "empty"
}

export function resolveSvgImageHref(source?: CardImageSource | null): string | null {
  return resolveCardImageSource(source)
}

export function warmGuardianCardAssets(): void {
  const urls = new Set<string>()
  urls.add(LOCAL_POWER_ICON)
  Object.values(LOCAL_ELEMENT_ICONS).forEach((u) => urls.add(u))
  Object.values(LOCAL_STYLE_ICONS).forEach((u) => urls.add(u))
  Object.values(LOCAL_ELEMENT_BACKGROUNDS).forEach((u) => urls.add(u))
  Object.values(LOCAL_ELEMENT_HEROES_STATIC).forEach((u) => urls.add(u))
  Object.values(LOCAL_ELEMENT_HEROES_ANIMATED).forEach((u) => u && urls.add(u))
  urls.forEach((href) => {
    const img = new Image()
    img.src = href
  })
}

export function resolvePowerIcon(assets: ElementCardAssets): string | null {
  return assets.powerIcon ?? LOCAL_POWER_ICON
}

export function resolveStyleIcon(
  style: PlayStyle,
  assets: ElementCardAssets
): string | null {
  return assets.styleIcon ?? LOCAL_STYLE_ICONS[style]
}

export function getCardAssets(element: CardElement): ElementCardAssets {
  return CARD_ELEMENT_ASSETS[element]
}
